import { CSSProperties, SVGProps, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    MOBILE_BOTTOM_NAV_ITEMS,
    MobileBottomNavIcon,
    MobileBottomNavItem,
} from "./MobileBottomNavData";
import { useCart } from "../../../../context/CartContext";
import "./MobileBottomNav.css";

type MobileBottomNavProps = {
    excludedPaths?: string[];
    items?: MobileBottomNavItem[];
    cartCount?: number;
};

type IconProps = SVGProps<SVGSVGElement>;

const HomeIcon = (props: IconProps) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
        <path
            fill="currentColor"
            d="M12 2.7c.35 0 .69.12.96.35l7.66 6.3c.46.38.73.95.73 1.55v7.78A2.32 2.32 0 0 1 19.03 21h-4.16a.82.82 0 0 1-.82-.82v-4.57a2.05 2.05 0 0 0-4.1 0v4.57a.82.82 0 0 1-.82.82H4.97a2.32 2.32 0 0 1-2.32-2.32V10.9c0-.6.27-1.17.73-1.55l7.66-6.3c.27-.23.61-.35.96-.35Z"
        />
    </svg>
);
const CategoriesIcon = (props: IconProps) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
        <rect x="3" y="3" width="7.5" height="7.5" rx="2.1" fill="currentColor" />
        <rect x="13.5" y="3" width="7.5" height="7.5" rx="2.1" fill="currentColor" />
        <rect x="3" y="13.5" width="7.5" height="7.5" rx="2.1" fill="currentColor" />
        <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2.1" fill="currentColor" />
    </svg>
);
const MoreIcon = (props: IconProps) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
        <rect x="2.75" y="5.25" width="18.5" height="13.5" rx="6.75" fill="currentColor" />
        <circle cx="7" cy="12" r="1.35" fill="#ffffff" />
        <circle cx="12" cy="12" r="1.35" fill="#ffffff" />
        <circle cx="17" cy="12" r="1.35" fill="#ffffff" />
    </svg>
);
const CartIcon = (props: IconProps) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
        <path
            fill="currentColor"
            d="M7.35 8.15V7.1a4.65 4.65 0 1 1 9.3 0v1.05h1.7a2.55 2.55 0 0 1 2.54 2.73l-.55 7.5A2.56 2.56 0 0 1 17.8 20.75H6.2a2.56 2.56 0 0 1-2.54-2.37l-.55-7.5a2.55 2.55 0 0 1 2.54-2.73h1.7Zm2.05 0h5.2V7.1a2.6 2.6 0 0 0-5.2 0v1.05Z"
        />
    </svg>
);
const ProfileIcon = (props: IconProps) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
        <circle cx="12" cy="8" r="4.05" fill="currentColor" />
        <path fill="currentColor"
            d="M4.1 20.15c.52-4.2 3.52-6.72 7.9-6.72s7.38 2.52 7.9 6.72c.06.48-.32.85-.79.85H4.89c-.47 0-.85-.37-.79-.85Z" />
    </svg>
);

const renderIcon = (icon: MobileBottomNavIcon) => {
    const iconProps = { className: "mobile-bottom-nav-svg" };
    switch (icon) {
        case "home": return <HomeIcon {...iconProps} />;
        case "categories": return <CategoriesIcon {...iconProps} />;
        case "more": return <MoreIcon {...iconProps} />;
        case "cart": return <CartIcon {...iconProps} />;
        case "profile": return <ProfileIcon {...iconProps} />;
        default: return null;
    }
};

const normalizePath = (pathname: string) => {
    if (!pathname) { return "/"; }
    return pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : pathname.toLowerCase();
};

const matchesPath = (currentPath: string, targetPath: string) => {
    const current = normalizePath(currentPath);
    const target = normalizePath(targetPath);
    if (target === "/homepage") {
        return (current === "/" || current === "/homepage");
    }
    if (target === "/categories") {
        return (current === "/categories" || current.startsWith("/categories/"));
    }
    return (current === target || current.startsWith(`${target}/`));
};

const MobileBottomNav = ({
    excludedPaths = [],
    items = MOBILE_BOTTOM_NAV_ITEMS,
    cartCount: cartCountProp,
}: MobileBottomNavProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { cartCount: contextCartCount } = useCart();
    const cartCount = cartCountProp ?? contextCartCount;

    const isExcluded = useMemo(
        () => excludedPaths.some((path) => matchesPath(location.pathname, path)),
        [excludedPaths, location.pathname]);

    const activeIndex = useMemo(() => {
        const index = items.findIndex((item) => matchesPath(location.pathname, item.path));
        return index >= 0 ? index : 0;
    }, [items, location.pathname]);

    const style = {
        "--mobile-nav-count": items.length,
        "--mobile-nav-active": activeIndex,
    } as CSSProperties;

    const handleNavigate = (item: MobileBottomNavItem) => {
        if (matchesPath(location.pathname, item.path)) { return; }
        navigate(item.path);
    };

    if (isExcluded) { return null; }
    return (
        <>
            <div className="mobile-bottom-nav-spacer" aria-hidden="true" />
            <nav className="mobile-bottom-nav" style={style} aria-label="Mobile navigation">
                <div className="mobile-bottom-nav-glass" aria-hidden="true" />
                <div className="mobile-bottom-nav-track">
                    <div className="mobile-bottom-nav-active-pill" aria-hidden="true" />
                    <div className="mobile-bottom-nav-items">
                        {items.map((item, index) => {
                            const active = index === activeIndex;
                            const badge = item.icon === "cart" ? cartCount : 0;
                            return (
                                <button key={item.id} type="button" className={`mobile-bottom-nav-item ${active ? "active" : ""}`}
                                    onClick={() => handleNavigate(item)}
                                    aria-current={active ? "page" : undefined}
                                    aria-label={item.label}>
                                    <span className="mobile-bottom-nav-icon">
                                        {renderIcon(item.icon)}
                                        {badge > 0 && (
                                            <span className="mobile-bottom-nav-badge"> {badge > 99 ? "99+" : badge}</span>
                                        )}
                                    </span>
                                    <span className="mobile-bottom-nav-label"> {item.label} </span>
                                </button>
                            );
                        }
                        )}
                    </div>
                </div>
            </nav>
        </>
    );
};

export default MobileBottomNav;
